import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { SignupSchema, type SignupInput } from '@cutlyft/zod-schemas';
import { useSignup } from '@/hooks/auth/use-signup';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

export default function SignupPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const signupMutation = useSignup();

  const form = useForm<SignupInput>({
    resolver: zodResolver(SignupSchema),
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      tenantName: '',
    },
  });

  const onSubmit = (data: SignupInput) => {
    signupMutation.mutate(data, {
      onSuccess: (res) => {
        setAuth(res.user, res.tenant, res.accessToken);
        navigate('/verify-email-pending');
      },
      onError: (err: any) => {
        // Map backend errors if returned in envelope
        const apiError = err.response?.data?.error;
        if (apiError?.code === 'EMAIL_ALREADY_EXISTS') {
          form.setError('email', { message: 'This email is already registered.' });
        } else {
          form.setError('root', {
            message: apiError?.message || 'Failed to sign up. Please try again.',
          });
        }
      },
    });
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg-secondary p-6 relative overflow-hidden">
      {/* Decorative premium gradient background glow */}
      <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-[120px] pointer-events-none" />

      <div className="w-full max-w-lg z-10 space-y-6">
        {/* Brand logo display */}
        <div className="text-center">
          <span className="text-3xl font-extrabold tracking-tight text-primary">Cutlyft</span>
          <p className="mt-2 text-sm text-fg-secondary">
            Precision 1D cutting stock optimisation for door and window fabricators
          </p>
        </div>

        <Card className="border border-border/50 shadow-xl bg-bg-elevated/80 backdrop-blur-md">
          <CardHeader>
            <CardTitle className="text-2xl text-fg-primary">Create Your Account</CardTitle>
            <CardDescription className="text-fg-secondary">
              Get started with Cutlyft. Complete the form to establish your workspace.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                {form.formState.errors.root && (
                  <div className="p-3 text-sm rounded bg-error-bg text-error border border-error/20">
                    {form.formState.errors.root.message}
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="tenantName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company / Tenant Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Acme Fabricators Ltd" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john.doe@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="••••••••" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full mt-2 bg-primary hover:bg-primary/95 text-primary-foreground font-semibold h-11"
                  disabled={signupMutation.isPending}
                >
                  {signupMutation.isPending ? 'Registering Workspace...' : 'Sign Up & Get Started'}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex justify-center border-t border-border/30 pt-6">
            <p className="text-sm text-fg-secondary">
              Already have a workspace?{' '}
              <Link to="/login" className="font-semibold text-primary hover:underline">
                Sign In
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
